import * as actions from '@redux-actions/';

import { getMockedFile } from '@utils/utils';

import URLS from '@utils/urlList';

import axios, { AxiosRequestConfig } from 'axios';
import {
    IAddToCartPayload,
    IContactLens,
    IContactLensDetailsPayload,
    IMySavedPrescriptionsList,
    IMySavedPrescriptionsListPayload,
    IPrescriptionFileResponse,
} from '@interfaces/contactsInterfaces';
import { mapClData, mapSavedPrescriptionsList } from '@utils/pages-utils/ContactsUtils';

const getContactsDetails = async (payload: IContactLensDetailsPayload) => {
    const { isMockRequest, storeId, catalogId, productId } = payload;
    let cl: IContactLens | null = null;

    if (isMockRequest) {
        const mySavedPrescriptions = '/data/mocks/contact_lens_attributes_by_sku.json';
        const result = await getMockedFile(mySavedPrescriptions);
        if (!!productId) cl = mapClData(result?.data);
    } else {
        const getContactAttributes = URLS.GET_CONTACT_ATTRIBUTES.replace(
            '{storeId}',
            storeId.toString()
        )
            .replace('{catalogId}', catalogId.toString())
            .replace('{productId}', productId.toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: getContactAttributes,
            headers: { 'Content-Type': 'application/json' },
        };

        const result = await axios(config);
        cl = mapClData(result?.data);
    }

    return cl;
};

const filterClPrescriptions = async (
    prescriptions: IMySavedPrescriptionsList,
    payload: IMySavedPrescriptionsListPayload
): Promise<IMySavedPrescriptionsList> => {
    const { isMockRequest, storeId, catalogId, productId: parentId } = payload;

    const clPrescriptions = prescriptions
        .filter(p => p.productCategory === 'CONTACTS') // filter out glasses prescriptions
        .filter(
            // filter out prescriptions whose lenses aren't parentId SKUs
            p => +parentId === +p.rightCatentryParentId || +parentId === +p.leftCatentryParentId
        );

    // request attributes for left and right lenses of each prescription
    // wait for all the requests to receive a response
    await Promise.all(
        clPrescriptions.map(async prescription => {
            prescription.leftCl = !!prescription.leftCatentryId
                ? await getContactsDetails({
                      isMockRequest,
                      storeId,
                      catalogId,
                      productId: prescription.leftCatentryId,
                  })
                : null;

            prescription.rightCl = !!prescription.rightCatentryId
                ? await getContactsDetails({
                      isMockRequest,
                      storeId,
                      catalogId,
                      productId: prescription.rightCatentryId,
                  })
                : null;
        })
    );

    return clPrescriptions;
};

// MY PRESCRIPTIONS

export const getContactsPrescriptions = (payload: IMySavedPrescriptionsListPayload) => {
    const { isMockRequest, storeId } = payload;

    if (isMockRequest) {
        const mySavedPrescriptions =
            '/data/mocks/my_saved_prescriptions_get_my_saved_prescriptions_mock.json';
        return dispatch => {
            dispatch(actions.getMySavedPrescriptionsPending());
            getMockedFile(mySavedPrescriptions)
                .then(result => {
                    if (result?.data?.resultList?.length > 0) {
                        const mappedSavedPrescriptions = mapSavedPrescriptionsList(
                            result?.data?.resultList
                        );

                        filterClPrescriptions(mappedSavedPrescriptions, payload).then(
                            prescriptions =>
                                dispatch(actions.getMySavedPrescriptionsSuccess(prescriptions))
                        );
                    } else {
                        dispatch(actions.getMySavedPrescriptionsSuccess([]));
                    }
                })
                .catch(() => dispatch(actions.getMySavedPrescriptionsError()));
        };
    } else {
        const mySavedPrescriptionsUrl = URLS.GET_MY_SAVED_PRESCRIPTIONS.replace(
            '{storeId}',
            storeId.toString()
        ).replace('{noCache}', new Date().getTime().toString());

        const config: AxiosRequestConfig = {
            method: 'GET',
            url: mySavedPrescriptionsUrl,
            headers: { 'Content-Type': 'application/json' },
        };

        return dispatch => {
            dispatch(actions.getMySavedPrescriptionsPending());
            axios(config)
                .then(result => {
                    if (result?.data?.resultList?.length > 0) {
                        const mappedSavedPrescriptions = mapSavedPrescriptionsList(
                            result?.data?.resultList
                        );

                        filterClPrescriptions(mappedSavedPrescriptions, payload).then(
                            prescriptions =>
                                dispatch(actions.getMySavedPrescriptionsSuccess(prescriptions))
                        );
                    } else {
                        dispatch(actions.getMySavedPrescriptionsSuccess([]));
                    }
                })
                .catch(() => dispatch(actions.getMySavedPrescriptionsError()));
        };
    }
};

// ADD TO CART

export const addToCart = (payload: IAddToCartPayload) => {
    const { isMockRequest, ...data } = payload;

    if (isMockRequest) {
        return dispatch => {
            console.log(data);
            dispatch(actions.addToCartSuccess());
        };
    } else {
        const addToCartUrl = URLS.ADD_TO_CART;

        const config: AxiosRequestConfig = {
            method: 'POST',
            url: addToCartUrl,
            headers: { 'Content-Type': 'application/json' },
            params: data,
        };

        return dispatch => {
            dispatch(actions.addToCartPending());
            axios(config)
                .then(result => {
                    console.log(result);
                    dispatch(actions.addToCartSuccess());
                })
                .catch(() => dispatch(actions.addToCartError()));
        };
    }
};

// DOWNLOAD PRESCRIPTION FILE

export const downloadPrescriptionFile = async (
    isMockRequest: boolean,
    storeId: string,
    fileName: string,
    controller: AbortController
): Promise<IPrescriptionFileResponse> => {
    return new Promise((resolve, reject) => {
        if (isMockRequest) {
            const downloadFileMock =
                '/data/mocks/my_prescription_download_prescription_file_mock.json';

            getMockedFile(downloadFileMock)
                .then(response => {
                    resolve(response.data);
                })
                .catch((error: any) => {
                    reject(error.response);
                });
        } else {
            const downloadFileUrl = URLS.DOWNLOAD_PRESCRIPTION_FILE.replace('{storeId}', storeId)
                .replace('{fileName}', fileName)
                .replace('{noCache}', new Date().getTime().toString());

            const config: AxiosRequestConfig = {
                method: 'GET',
                url: downloadFileUrl,
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
            };

            axios(config)
                .then((response: any) => {
                    resolve(response.data);
                })
                .catch((error: any) => {
                    reject(error.response);
                });
        }
    });
};
